import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast from "../../components/CustomComponents/toast/toast";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import { setFilterField } from "../../app/itemGridPreferencesSlice";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import {
  labelForCancelReasonCode,
  labelForCancelledByType,
} from "../../utils/marketplaceCancelReasonLabels";
import "../order/ManageOrderFilters.css";
import "./restaurantPerformanceReport.css";

const PAGE_KEY = "restaurantPerformanceReport";

const toValidDate = (value) => {
  if (value == null) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const pct = (part, whole) => {
  if (!whole || whole <= 0) return 0;
  return Math.round((part / whole) * 1000) / 10;
};

const fmtMinutes = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : `${Number(n).toFixed(1)} min`;

const RestaurantPerformanceReport = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const currencySymbols = { GBP: "£", USD: "$", EUR: "€", PKR: "Rs" };
  const currencySymbol = currencySymbols[compCurrency] || compCurrency || "";

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const defaultDateRange = useMemo(
    () => [moment().startOf("day").toDate(), moment().endOf("day").toDate()],
    []
  );

  const preferences = useSelector((state) => state.itemGridPreferences[PAGE_KEY] || {});
  const dateRange = preferences.dateRange || defaultDateRange;
  const rawStartDate = dateRange?.[0] ?? null;
  const rawEndDate = dateRange?.[1] ?? null;
  const startDate = toValidDate(rawStartDate) || defaultDateRange[0];
  const endDate = toValidDate(rawEndDate) || defaultDateRange[1];
  const startKey = moment(startDate).format("YYYY-MM-DD");
  const endKey = moment(endDate).format("YYYY-MM-DD");

  const money = (v) =>
    `${currencySymbol}${Number(v ?? 0)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

  const handleDateRangeChange = useCallback(
    (update) => {
      const [newStartDate, newEndDate] = Array.isArray(update) ? update : [update, null];
      if (
        (!newStartDate || !moment(newStartDate).isValid()) &&
        (!newEndDate || !moment(newEndDate).isValid())
      ) {
        dispatch(
          setFilterField({
            pageKey: PAGE_KEY,
            field: "dateRange",
            value: defaultDateRange,
          })
        );
        return;
      }
      if (
        (!newStartDate || moment(newStartDate).isValid()) &&
        (!newEndDate || moment(newEndDate).isValid())
      ) {
        let effectiveStartDate = newStartDate;
        let effectiveEndDate = newEndDate;
        if (newStartDate && newEndDate && newStartDate > newEndDate) {
          [effectiveStartDate, effectiveEndDate] = [newEndDate, newStartDate];
        } else if (newStartDate && !newEndDate) {
          effectiveEndDate = new Date(newStartDate.getTime());
          effectiveEndDate.setHours(23, 59, 59, 999);
        }
        dispatch(
          setFilterField({
            pageKey: PAGE_KEY,
            field: "dateRange",
            value: [effectiveStartDate, effectiveEndDate],
          })
        );
      }
    },
    [dispatch, defaultDateRange]
  );

  const clearAllFilters = () => {
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "dateRange", value: defaultDateRange }));
  };

  const fetchData = useCallback(async () => {
    if (!CompID || Number(CompID) <= 0) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_RESTAURANT_PERFORMANCE, {
        params: {
          CompID: Number(CompID),
          FromDate: startKey,
          ToDate: endKey,
        },
      });
      if (response.data?.status || response.data?.Status) {
        setSummary(response.data.data || response.data.Data || null);
      } else {
        setSummary(null);
        showErrorToast(
          response.data?.message || response.data?.Message || "Failed to load restaurant performance"
        );
      }
    } catch (err) {
      setSummary(null);
      showErrorToast(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          "Failed to load restaurant performance"
      );
    } finally {
      setLoading(false);
    }
  }, [CompID, startKey, endKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const s = summary || {};
  const total = num(s.totalOrders ?? s.TotalOrders);
  const completed = num(s.completedOrders ?? s.CompletedOrders);
  const cancelled = num(s.cancelledOrders ?? s.CancelledOrders);
  const failedReturns = num(s.failedDeliveryReturns ?? s.FailedDeliveryReturns);
  const cancelRate = pct(cancelled, total);
  const completeRate = pct(completed, total);

  const sources = useMemo(
    () => [
      {
        key: "Restaurant",
        count: num(s.restaurantCausedCancels ?? s.RestaurantCausedCancels),
        color: "#b45309",
      },
      {
        key: "Customer",
        count: num(s.customerCausedCancels ?? s.CustomerCausedCancels),
        color: "#0d9488",
      },
      {
        key: "Rider",
        count: num(s.riderCausedCancels ?? s.RiderCausedCancels),
        color: "#7c3aed",
      },
      {
        key: "System",
        count: num(s.systemCausedCancels ?? s.SystemCausedCancels),
        color: "#6b7280",
      },
    ],
    [s]
  );

  const attributed = sources.reduce((sum, row) => sum + row.count, 0);
  const unattributed = Math.max(0, cancelled - attributed);

  const breakdown = useMemo(() => {
    const rows = s.cancelBreakdown || s.CancelBreakdown || [];
    const max = rows.reduce(
      (m, row) => Math.max(m, num(row.cancelCount ?? row.CancelCount)),
      0
    );
    return rows.map((row) => {
      const code = row.cancelReasonCode ?? row.CancelReasonCode ?? "";
      const label = labelForCancelReasonCode(code) || code || "—";
      const typeRaw = row.cancelledByType ?? row.CancelledByType;
      const count = num(row.cancelCount ?? row.CancelCount);
      return {
        code,
        label,
        typeLabel: labelForCancelledByType(typeRaw),
        unattributed: !typeRaw || String(typeRaw).toLowerCase() === "unknown",
        count,
        share: pct(count, cancelled || max || 1),
        barWidth: max > 0 ? (count / max) * 100 : 0,
      };
    });
  }, [s, cancelled]);

  const primaryBtn = {
    background: themeColor,
    boxShadow: `0 1px 3px ${themeColor}55`,
  };

  return (
    <div className={`rp-page ${isDarkMode ? "is-dark" : "is-light"}`}>
      {loading && <Preloader />}

      <div className="d-flex align-items-start mb-3 flex-wrap gap-3">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <a href="#/report">Report</a>
            </li>
            <li className="breadcrumb-item active">Restaurant Performance</li>
          </ol>
          <h1 className="page-header mb-1">Restaurant Performance</h1>
          <p className="rp-desc">
            Cancel rates, kitchen time, and delivery speed for your Go Fatafat orders.
          </p>
        </div>
      </div>

      <div className="rp-toolbar">
        <div className="rp-field">
          <label>Date range</label>
          <div className="filter-control" style={{ position: "relative", zIndex: 20, width: "100%" }}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateRangeChange}
              placeholder="Select Date Range"
              className="filter-dropdown"
              allowSingleDate={true}
              allowDateRange={true}
              showTimeSelection={false}
            />
          </div>
        </div>
        <div className="rp-toolbar-actions">
          <button type="button" className="rp-btn-ghost" onClick={clearAllFilters}>
            Reset
          </button>
          <button type="button" className="rp-btn-ghost" onClick={fetchData}>
            Refresh
          </button>
          <button type="button" className="rp-btn-primary" style={primaryBtn} onClick={fetchData}>
            Apply
          </button>
        </div>
      </div>

      {!summary && !loading ? (
        <div className="rp-panel">
          <div className="rp-empty">No performance data for the selected date range.</div>
        </div>
      ) : summary ? (
        <>
          <div className="rp-hero">
            <div className="rp-panel">
              <h2 className="rp-panel-title">Orders</h2>
              <div className="rp-order-grid">
                <div>
                  <div className="rp-metric-label">Total</div>
                  <div className="rp-metric-value">{total}</div>
                </div>
                <div>
                  <div className="rp-metric-label">Completed</div>
                  <div className="rp-metric-value is-ok">{completed}</div>
                </div>
                <div>
                  <div className="rp-metric-label">Cancelled / Refunded</div>
                  <div className="rp-metric-value is-danger">{cancelled}</div>
                </div>
                <div>
                  <div className="rp-metric-label">Failed delivery returns</div>
                  <div
                    className={`rp-metric-value ${failedReturns > 0 ? "is-danger" : "is-muted"}`}
                  >
                    {failedReturns}
                  </div>
                </div>
              </div>
              <div className="rp-rate-row">
                <span className="rp-rate-label">Cancel rate</span>
                <span className="rp-rate-pct">{cancelRate}%</span>
              </div>
              <div className="rp-bar-track" aria-hidden>
                <div
                  className="rp-bar-fill"
                  style={{
                    width: `${Math.min(100, cancelRate)}%`,
                    background: "#b91c1c",
                  }}
                />
              </div>
              <div className="rp-rate-row mt-3">
                <span className="rp-rate-label">Completion rate</span>
                <span className="rp-rate-pct">{completeRate}%</span>
              </div>
              <div className="rp-bar-track" aria-hidden>
                <div
                  className="rp-bar-fill"
                  style={{
                    width: `${Math.min(100, completeRate)}%`,
                    background: "#047857",
                  }}
                />
              </div>
            </div>

            <div className="rp-panel">
              <h2 className="rp-panel-title">Kitchen & delivery times</h2>
              <div className="rp-timing-list">
                <div className="rp-timing-row">
                  <span className="rp-timing-label">Avg placed → accept</span>
                  <span className="rp-timing-value">
                    {fmtMinutes(s.avgPlacedToAcceptMinutes ?? s.AvgPlacedToAcceptMinutes)}
                  </span>
                </div>
                <div className="rp-timing-row">
                  <span className="rp-timing-label">Avg accept → ready</span>
                  <span className="rp-timing-value">
                    {fmtMinutes(s.avgAcceptToReadyMinutes ?? s.AvgAcceptToReadyMinutes)}
                  </span>
                </div>
                <div className="rp-timing-row">
                  <span className="rp-timing-label">Avg ready → complete</span>
                  <span className="rp-timing-value">
                    {fmtMinutes(s.avgReadyToCompleteMinutes ?? s.AvgReadyToCompleteMinutes)}
                  </span>
                </div>
                <div className="rp-money-row">
                  <span className="rp-timing-label">Gross amount</span>
                  <span className="rp-timing-value">
                    {money(s.grossOrderAmount ?? s.GrossOrderAmount)}
                  </span>
                </div>
                <div className="rp-money-row">
                  <span className="rp-timing-label">Completed amount</span>
                  <span className="rp-timing-value is-ok">
                    {money(s.completedOrderAmount ?? s.CompletedOrderAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="rp-grid-2">
            <div className="rp-panel">
              <h2 className="rp-panel-title">Who cancelled</h2>
              <div className="rp-cancel-sources">
                {sources.map((row) => (
                  <div className="rp-source-row" key={row.key}>
                    <span className="rp-source-name">{row.key}</span>
                    <div className="rp-bar-track" aria-hidden>
                      <div
                        className="rp-bar-fill"
                        style={{
                          width: `${cancelled > 0 ? (row.count / cancelled) * 100 : 0}%`,
                          background: row.color,
                        }}
                      />
                    </div>
                    <span className="rp-source-count">{row.count}</span>
                  </div>
                ))}
                {unattributed > 0 && (
                  <div className="rp-source-row">
                    <span className="rp-source-name" style={{ color: "#9ca3af" }}>
                      Unattributed
                    </span>
                    <div className="rp-bar-track" aria-hidden>
                      <div
                        className="rp-bar-fill"
                        style={{
                          width: `${(unattributed / cancelled) * 100}%`,
                          background: "#d1d5db",
                        }}
                      />
                    </div>
                    <span className="rp-source-count">{unattributed}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="rp-panel">
              <h2 className="rp-panel-title">Cancel breakdown</h2>
              {breakdown.length === 0 ? (
                <div className="rp-empty">No cancels in this date range.</div>
              ) : (
                <div className="table-responsive">
                  <table className="rp-table">
                    <thead>
                      <tr>
                        <th>Reason</th>
                        <th>By</th>
                        <th>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdown.map((row, idx) => (
                        <tr key={`${row.code}-${row.typeLabel}-${idx}`}>
                          <td className="rp-reason-cell">
                            <span className="rp-reason-main">{row.label}</span>
                            {row.label !== row.code && row.code ? (
                              <span className="rp-reason-code">{row.code}</span>
                            ) : null}
                          </td>
                          <td>
                            <span
                              className={`rp-type-pill ${row.unattributed ? "is-unattributed" : ""}`}
                            >
                              {row.typeLabel}
                            </span>
                          </td>
                          <td className="rp-count-cell">
                            <div className="rp-count-top">
                              <span className="rp-count-num">{row.count}</span>
                              <span className="rp-count-share">{row.share}%</span>
                            </div>
                            <div className="rp-bar-track" aria-hidden>
                              <div
                                className="rp-bar-fill"
                                style={{
                                  width: `${row.barWidth}%`,
                                  background: themeColor,
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default RestaurantPerformanceReport;
